import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "outer" }
const _hoisted_2 = { class: "lat-lon" }
const _hoisted_3 = { class: "coords" }
const _hoisted_4 = { class: "action" }
const _hoisted_5 = {
  key: 0,
  class: "osm-attribution"
}
const _hoisted_6 = ["href"]
const _hoisted_7 = { key: 2 }
const _hoisted_8 = ["disabled", "onClick", "onKeypress"]

import { computed, ref, onMounted } from 'vue';

import axios from '@nextcloud/axios';
import { showError } from '@services/utils/dialog';
import { config } from '@services/user-config';
import { t } from '@services/l10n';

import NcActions from '@nextcloud/vue/components/NcActions';
import NcActionButton from '@nextcloud/vue/components/NcActionButton';
import NcTextField from '@nextcloud/vue/components/NcTextField';

import type { IPhoto } from '@typings';

import MagnifyIcon from 'vue-material-design-icons/Magnify.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import UndoIcon from 'vue-material-design-icons/UndoVariant.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';

type NLocation = {
  osm_id: number;
  type?: string;
  icon?: string;
  display_name: string;
  lat: string;
  lon: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'EditLocation',
  props: {
    photos: {},
    disabled: { type: Boolean }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const dirty = ref(false);
const lat = ref<number | null>(null);
const lon = ref<number | null>(null);
const searchBar = ref('');
const loading = ref(false);

const options = ref<NLocation[]>([]);

const loc = computed(() => {
  if (lat.value && lon.value) {
    return `${lat.value.toFixed(6)}, ${lon.value.toFixed(6)}`;
  }
  return t('memories', 'No coordinates');
});

const searchBase = computed(() => config.places_search_url.trim());
const isNominatim = computed(() => searchBase.value.toLowerCase().includes('nominatim'));

onMounted(() => {
  reset();
});

function reset() {
  dirty.value = false;
  const photos = props.photos as IPhoto[];

  let latSum = 0,
    lonSum = 0,
    count = 0;
  for (const photo of photos) {
    const exif = photo.imageInfo?.exif;
    if (!exif) {
      continue;
    }

    if (exif.GPSLatitude && exif.GPSLongitude) {
      latSum += Number(exif.GPSLatitude);
      lonSum += Number(exif.GPSLongitude);
      count++;
    }
  }

  if (count > 0) {
    lat.value = latSum / count;
    lon.value = lonSum / count;
  } else {
    lat.value = lon.value = null;
  }
}

async function search() {
  if (loading.value || searchBar.value.length === 0) {
    return;
  }

  // Check if searchbar is already a coordinate
  const coords = searchBar.value.split(',');
  if (coords.length === 2) {
    const coordLat = Number(coords[0].trim());
    const coordLon = Number(coords[1].trim());
    if (!isNaN(coordLat) && !isNaN(coordLon)) {
      return select({
        osm_id: 0,
        display_name: `${coordLat.toFixed(6)}, ${coordLon.toFixed(6)}`,
        lat: coordLat.toFixed(6),
        lon: coordLon.toFixed(6),
      });
    }
  }

  // No search provider configured.
  if (!searchBase.value) return;

  loading.value = true;
  const q = window.encodeURIComponent(searchBar.value);
  try {
    const response = await axios.get<NLocation[]>(`${searchBase.value}/search?q=${q}&format=jsonv2`);
    options.value = response.data.filter((x) => x.lat && x.lon && x.display_name);
  } catch (error) {
    console.error(error);
    showError(t('memories', 'Failed to search for location.'));
  } finally {
    loading.value = false;
  }
}

function clear() {
  dirty.value = true;
  lat.value = 0;
  lon.value = 0;
}

function select(option: NLocation) {
  dirty.value = true;
  lat.value = Number(option.lat);
  lon.value = Number(option.lon);
  options.value = [];
  searchBar.value = '';
}

function result() {
  if (!dirty.value || lat.value === null || lon.value === null) return null;

  const resultLat = lat.value.toFixed(6);
  const resultLon = lon.value.toFixed(6);

  // Exiftool is actually supposed to pick up the reference from
  // a signed set of coordinates: https://exiftool.org/faq.html#Q14
  // But it doesn't seem to work for some very specific files, so
  // we'll just set it manually to N/S and E/W
  return {
    GPSLatitude: resultLat,
    GPSLongitude: resultLon,
    GPSLatitudeRef: lat.value >= 0 ? 'N' : 'S',
    GPSLongitudeRef: lon.value >= 0 ? 'E' : 'W',
    GPSCoordinates: `${resultLat}, ${resultLon}`,
  };
}

__expose({ result });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", null, _toDisplayString(loc.value), 1),
        _createTextVNode(" " + _toDisplayString(dirty.value ? '*' : ''), 1)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_unref(NcActions), { inline: 2 }, {
          default: _withCtx(() => [
            (dirty.value)
              ? (_openBlock(), _createBlock(_unref(NcActionButton), {
                  key: 0,
                  "aria-label": _unref(t)('memories', 'Reset'),
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (reset())),
                  disabled: __props.disabled
                }, {
                  icon: _withCtx(() => [
                    _createVNode(UndoIcon, { size: 20 })
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Reset')) + " ", 1)
                  ]),
                  _: 1
                }, 8, ["aria-label", "disabled"]))
              : _createCommentVNode("", true),
            (lat.value && lon.value)
              ? (_openBlock(), _createBlock(_unref(NcActionButton), {
                  key: 1,
                  "aria-label": _unref(t)('memories', 'Remove location'),
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (clear())),
                  disabled: __props.disabled
                }, {
                  icon: _withCtx(() => [
                    _createVNode(CloseIcon, { size: 20 })
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Remove location')) + " ", 1)
                  ]),
                  _: 1
                }, 8, ["aria-label", "disabled"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(_unref(NcTextField), {
      modelValue: searchBar.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchBar).value = $event)),
      label: _unref(t)('memories', 'Search'),
      placeholder: _unref(t)('memories', 'Search location / landmark'),
      disabled: __props.disabled,
      "trailing-button-icon": "arrowEnd",
      "show-trailing-button": searchBar.value.length > 0 && !loading.value,
      onTrailingButtonClick: search,
      onKeypress: _withKeys(search, ["enter"])
    }, {
      default: _withCtx(() => [
        _createVNode(MagnifyIcon, { size: 16 })
      ]),
      _: 1
    }, 8, ["modelValue", "label", "placeholder", "disabled", "show-trailing-button"]),
    (isNominatim.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _cache[3] || (_cache[3] = _createTextVNode(" Powered by ", -1)),
          _createElementVNode("a", {
            href: searchBase.value,
            target: "_blank"
          }, "Nominatim", 8, _hoisted_6),
          _cache[4] || (_cache[4] = _createTextVNode(" © ", -1)),
          _cache[5] || (_cache[5] = _createElementVNode("a", {
            href: "https://www.openstreetmap.org/copyright",
            target: "_blank"
          }, "OpenStreetMap", -1)),
          _cache[6] || (_cache[6] = _createTextVNode(" contributors ", -1))
        ]))
      : _createCommentVNode("", true),
    (loading.value)
      ? (_openBlock(), _createBlock(XLoadingIcon, {
          key: 1,
          class: "loading-spinner"
        }))
      : _createCommentVNode("", true),
    (options.value.length > 0)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_7, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option) => {
            return (_openBlock(), _createElementBlock("li", {
              key: option.osm_id,
              disabled: __props.disabled,
              onClick: ($event: any) => (select(option)),
              onKeypress: _withKeys(($event: any) => (select(option)), ["enter"]),
              tabindex: "0"
            }, _toDisplayString(option.display_name), 41, _hoisted_8))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})